import React, { useState } from 'react';
import {
	AppBar,
	Toolbar,
	Box,
	Typography,
	IconButton,
	Divider,
	Chip,
	Avatar,
	Badge,
	Popover,
	List,
	ListItem,
	ListItemText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { USER_ROLES } from '../Utils/Enums';
import { getElderHomeName, getUserName, getUserRole } from '../Utils/AuthUtil';
import { useNavigate } from 'react-router-dom';
import TopBarDropdownMenu from './TopBarDropdownMenu';
import Logo from '../assets/images/NewLogo.png';
import { getNotifications } from '../context/MainProvider';
import { dark, teal } from '../assets/theme/Colors';

export default function TopNavigationBar(props) {
	const { onBack, onForward, title } = props;
	const navigator = useNavigate();
	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);
	const [anchorElTwo, setAnchorElTwo] = useState(null);
	const openNotification = Boolean(anchorElTwo);
	const homeName = getElderHomeName();
	const userInitials = getUserName()
		.split(' ')
		.map(word => word[0])
		.join('')
		.toUpperCase()
		.slice(0, 2);
	const { notifications } = getNotifications();

	function getCurrentUserRole() {
		const role = parseInt(getUserRole());
		if (role === USER_ROLES.superAdmin) {
			return 'Super Admin';
		} else if (role === USER_ROLES.admin) {
			return 'Admin';
		} else if (role === USER_ROLES.staff) {
			return 'Staff';
		}

		return 'N/A';
	}

	const handleClick = event => {
		setAnchorEl(event.currentTarget);
	};

	function onLogout() {
		localStorage.clear();
		navigator('/');
	}

	const handleOpen = event => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleOpenTwo = event => {
		setAnchorElTwo(event.currentTarget);
	};

	const handleCloseTwo = () => {
		setAnchorElTwo(null);
	};

	const formatMessage = msg => {
		const match = msg.match(/for (.*?) by (.*)/);

		if (!match) return msg;

		const resident = match[1];
		const user = match[2];

		const [beforeResident] = msg.split(resident);
		return (
			<>
				{beforeResident}
				<Box component="span" sx={{ fontWeight: 600, color: teal }}>
					{resident}
				</Box>{' '}
				by{' '}
				<Box
					component="span"
					sx={{ fontWeight: 600, color: '#6b8888' }}>
					{user}
				</Box>
			</>
		);
	};

	return (
		<AppBar
			position="fixed"
			elevation={0}
			sx={{
				bgcolor: dark,
				borderBottom: '1px solid rgba(0,196,180,0.2)',
				zIndex: 1300,
			}}>
			<Toolbar
				sx={{
					minHeight: '54px !important',
					'@media (min-width:600px)': {
						minHeight: '54px !important',
					},
					gap: 1,
				}}>
				{/* LOGO */}
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
					}}>
					<Box
						component="img"
						src={Logo}
						alt="SenzCare.AI"
						sx={{ height: 32 }}
					/>
				</Box>

				<Divider
					orientation="vertical"
					flexItem
					sx={{ bgcolor: 'rgba(255,255,255,0.15)', mx: 1 }}
				/>

				{/* BACK */}
				<IconButton
					onClick={onBack}
					sx={{
						width: 30,
						height: 30,
						border: '1px solid rgba(255,255,255,0.18)',
						borderRadius: 1,
						color: '#fff',
					}}>
					<ArrowBackIcon sx={{ color: '#fff' }} fontSize="small" />
				</IconButton>

				{/* FORWARD */}
				<IconButton
					onClick={onForward}
					sx={{
						width: 30,
						height: 30,
						border: '1px solid rgba(255,255,255,0.18)',
						borderRadius: 1,
						color: '#fff',
					}}>
					<ArrowForwardIcon sx={{ color: '#fff' }} fontSize="small" />
				</IconButton>

				{/* TITLE */}
				<Typography
					sx={{
						fontSize: 13,
						color: 'rgba(255,255,255,0.6)',
						ml: 1,
					}}>
					<b style={{ color: '#fff', fontWeight: 500 }}>
						{title || 'Dashboard'}
					</b>
				</Typography>

				<Box sx={{ flexGrow: 1 }} />

				<>
					{/* NOTIFICATIONS */}
					<IconButton
						onClick={handleOpenTwo}
						sx={{
							width: 34,
							height: 34,
							border: '1px solid rgba(255,255,255,0.2)',
							color: '#fff',
						}}>
						{notifications && notifications.length > 0 ? (
							<Badge
								variant="dot"
								color="error"
								sx={{
									'& .MuiBadge-badge': {
										width: 8,
										height: 8,
									},
								}}>
								<NotificationsIcon
									sx={{ color: '#fff' }}
									fontSize="small"
								/>
							</Badge>
						) : (
							<NotificationsIcon
								sx={{ color: '#fff' }}
								fontSize="small"
							/>
						)}
					</IconButton>
					<Popover
						open={Boolean(anchorElTwo)}
						anchorEl={anchorElTwo}
						onClose={handleCloseTwo}
						anchorOrigin={{
							vertical: 'bottom',
							horizontal: 'right',
						}}
						transformOrigin={{
							vertical: 'top',
							horizontal: 'right',
						}}
						slotProps={{
							paper: {
								sx: {
									mt: 0.75, // ~6px gap below button
									width: 300,
									p: 2,
									borderRadius: 2,
								},
							},
						}}>
						<Box p={1}>
							<Typography
								variant="subtitle1"
								fontWeight="bold"
								mb={1}>
								Notifications
							</Typography>

							<Box
								sx={{
									maxHeight:
										notifications?.length > 5
											? 240
											: 'auto',
									overflowY:
										notifications?.length > 5
											? 'auto'
											: 'visible',
								}}>
								{notifications && notifications.length > 0 ? (
									<List dense disablePadding>
										{notifications.map((msg, index) => (
											<Box key={index}>
												<ListItem sx={{ px: 0 }}>
													<ListItemText
														primary={formatMessage(
															msg,
														)}
														primaryTypographyProps={{
															fontSize: 14,
														}}
													/>
												</ListItem>

												{index !==
													notifications.length -
														1 && <Divider />}
											</Box>
										))}
									</List>
								) : (
									<Typography
										variant="body2"
										color="text.secondary">
										No notifications
									</Typography>
								)}
							</Box>
						</Box>
					</Popover>
				</>

				{/* ORG */}
				<Chip
					label={homeName}
					size="small"
					sx={{
						bgcolor: 'rgba(255,255,255,0.07)',
						color: '#fff',
						border: '1px solid rgba(255,255,255,0.12)',
						mx: 1,
					}}
				/>

				{/* AVATAR */}
				<Avatar
					onClick={e => handleClick(e)}
					sx={{
						width: 34,
						height: 34,
						fontSize: 12,
						fontWeight: 600,
						bgcolor: '#1f2937',
						border: '2px solid rgba(0,196,180,0.3)',
						cursor: 'pointer',
					}}>
					{userInitials}
				</Avatar>
				<TopBarDropdownMenu
					anchorEl={anchorEl}
					open={open}
					handleClose={() => setAnchorEl(null)}
					onLogout={onLogout}
					userRole={getCurrentUserRole()}
				/>
			</Toolbar>
		</AppBar>
	);
}
